import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' }) : '-';
const isPdf = (url) => url && url.toLowerCase().includes('.pdf');

// Public, mobile-first page the manager opens from the LINE message:
// shows the transfer destination (copiable bank info + downloadable QR) and lets
// them attach the receipt. Once a slip is attached it says so super clearly.
export default function RefundReceiptPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [showReplaceForm, setShowReplaceForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [copied, setCopied] = useState(null);
  const fileRef = useRef();
  // Withdrawal: owner can't transfer (bank info wrong) → return funds to the customer.
  const [returning, setReturning] = useState(false);
  const [returnError, setReturnError] = useState(null);
  const [returned, setReturned] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/money/refund/upload-slip/${token}`)
      .then((r) => setInfo(r.data))
      .catch((err) => setFetchError(err.response?.data?.message || 'ไม่พบรายการหรือ Link หมดอายุ'));
  }, [token]);

  const copy = (key, text) => {
    navigator.clipboard.writeText(String(text)).then(() => {
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
    }).catch(() => {});
  };

  const downloadQr = async () => {
    try {
      const res = await fetch(info.qrUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'promptpay-qr.jpg';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      window.open(info.qrUrl, '_blank'); // CORS fallback — open so they can long-press save
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files[0];
    if (!file) return;
    // Cancelled withdrawal: the customer was already refunded, so uploading a slip means
    // the owner transferred anyway → this re-charges the wallet. Confirm the money move.
    if (info?.withdrawalCancelled &&
      !window.confirm('ลูกค้ายกเลิกคำขอนี้แล้ว (เงินถูกคืนเข้ากระเป๋าลูกค้าไปแล้ว) การอัปโหลดสลิปจะหักเงินจำนวนนี้คืนจากกระเป๋าเงินของลูกค้า ยืนยันหรือไม่?')) {
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('slip', file);
      const res = await axios.post(
        `${BACKEND_URL}/api/money/refund/upload-slip/${token}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setInfo((prev) => ({ ...prev, slipUrl: res.data.slipUrl, status: 'transferred', transferredAt: new Date().toISOString(), withdrawalCancelled: false }));
      setShowReplaceForm(false);
    } catch (err) {
      setUploadError(err.response?.data?.message || 'อัปโหลดไม่สำเร็จ กรุณาลองใหม่');
    } finally {
      setUploading(false);
    }
  };

  const handleReturn = async () => {
    if (!window.confirm('ยืนยันว่าโอนเงินให้ลูกค้าไม่ได้ (ข้อมูลบัญชีไม่ถูกต้อง)?\nระบบจะคืนเงินเข้ากระเป๋าเงินของลูกค้า และแจ้งลูกค้าให้แก้ไขบัญชี')) return;
    setReturning(true);
    setReturnError(null);
    try {
      await axios.post(`${BACKEND_URL}/api/money/refund/upload-slip/${token}/return`, {});
      setReturned(true);
    } catch (err) {
      setReturnError(err.response?.data?.message || 'คืนเงินไม่สำเร็จ กรุณาลองใหม่');
    } finally {
      setReturning(false);
    }
  };

  if (fetchError) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <p className="text-red-600 font-semibold text-lg mb-2">ไม่สามารถเปิดหน้านี้ได้</p>
          <p className="text-gray-500 text-sm">{fetchError}</p>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">กำลังโหลด...</p>
      </div>
    );
  }

  // The refund was cancelled (soft-deleted) after this link went out — tell the
  // manager clearly so they don't transfer. Shown instead of the normal page.
  if (info.deleted) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">🚫</div>
          <p className="text-red-600 font-bold text-xl mb-2">รายการนี้ถูกยกเลิกแล้ว</p>
          <p className="text-gray-600 text-sm">ไม่ต้องโอนเงิน — รายการโอนเงินคืนนี้ถูกลบโดยแอดมิน</p>
          {info.contact && <p className="text-gray-400 text-xs mt-3">{info.contact}{info.smileName ? ` (${info.smileName})` : ''}</p>}
        </div>
      </div>
    );
  }

  // Owner marked the withdrawal un-transferable — funds returned to the customer.
  // Also on re-opening the link later: it used to show the upload form again, and a
  // slip there marked the withdrawal paid without taking the money back (QA 823).
  if (returned || info.status === 'returned') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">↩️</div>
          <p className="text-gray-800 font-bold text-xl mb-2">คืนเงินให้ลูกค้าแล้ว</p>
          <p className="text-gray-600 text-sm">เงินถูกคืนเข้ากระเป๋าเงินของลูกค้า และแจ้งลูกค้าให้ตรวจสอบข้อมูลบัญชีแล้ว — ไม่ต้องโอน</p>
        </div>
      </div>
    );
  }

  const hasSlip = !!info.slipUrl;
  // Reverted to pending_info = admin is re-entering the bank details. The link stays
  // live but must not show the (now stale) account, so the customer doesn't transfer.
  const isEditing = info.status === 'pending_info';

  return (
    <div className="min-h-screen bg-gray-100 flex justify-center p-3">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full overflow-hidden self-start">

        {/* Header */}
        <div className="bg-blue-900 px-5 py-4">
          <p className="text-blue-300 text-xs mb-0.5">โอนเงินคืนลูกค้า</p>
          <p className="text-white text-lg font-bold leading-tight">{info.contact || '-'}</p>
          {info.smileName && <p className="text-blue-200 text-sm">{info.smileName}</p>}
          {info.isCompanyOrder != null && (
            <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-semibold ${info.isCompanyOrder === false ? 'bg-blue-100 text-blue-800' : 'bg-amber-300 text-amber-900'}`}>
              {info.isCompanyOrder === true ? '🔗 จ่ายผ่านลิ้งค์'
                : info.isCompanyOrder === 'mixed' ? '🔗 จ่ายผ่านลิ้งค์ + 🏦 โอนธนาคาร'
                : '🏦 โอนธนาคาร'}
            </span>
          )}
        </div>

        {/* Amount */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-baseline justify-between">
          <span className="text-gray-500 text-sm">ยอดโอน</span>
          <span className="text-3xl font-extrabold text-blue-900">฿{fmt(info.amount)}</span>
        </div>

        {/* Cancelled-withdrawal warning: funds already returned to the customer's wallet */}
        {info.withdrawalCancelled && (
          <div className="mx-5 mt-4 rounded-xl bg-amber-50 border border-amber-300 px-4 py-3">
            <p className="text-amber-800 font-bold text-sm">⚠️ ลูกค้ายกเลิกคำขอถอนเงินนี้แล้ว</p>
            <p className="text-amber-700 text-xs mt-0.5">เงินถูกคืนเข้ากระเป๋าเงินของลูกค้าแล้ว — <b>ไม่ต้องโอน</b> อัปโหลดสลิปเฉพาะกรณีที่คุณโอนไปแล้วจริง (ระบบจะหักเงินคืนจากลูกค้า)</p>
          </div>
        )}

        {/* Already-attached banner */}
        {hasSlip && (
          <div className="mx-5 mt-4 rounded-xl bg-green-50 border border-green-300 px-4 py-3 text-center">
            <p className="text-green-700 font-bold text-lg">✅ แนบสลิปแล้ว</p>
            <p className="text-green-600 text-xs mt-0.5">โอนเมื่อ {fmtDate(info.transferredAt)}</p>
          </div>
        )}

        <div className="px-5 py-4 space-y-4">
          {hasSlip && !showReplaceForm ? (
            <div className="space-y-3">
              {isPdf(info.slipUrl) ? (
                <a href={info.slipUrl} target="_blank" rel="noopener noreferrer"
                  className="block w-full text-center py-3 rounded-xl border border-blue-300 text-blue-700 font-medium">ดูสลิป PDF</a>
              ) : (
                <a href={info.slipUrl} target="_blank" rel="noopener noreferrer">
                  <img src={info.slipUrl} alt="สลิป" className="w-full rounded-xl border border-gray-200 object-contain max-h-72" />
                </a>
              )}
              <button onClick={() => setShowReplaceForm(true)}
                className="w-full py-2.5 rounded-xl border border-gray-300 text-gray-500 text-sm">📎 แนบสลิปใหม่แทนที่</button>
            </div>
          ) : isEditing ? (
            <div className="rounded-xl bg-amber-50 border border-amber-300 px-4 py-5 text-center space-y-1">
              <p className="text-amber-700 font-bold text-lg">⏳ ข้อมูลกำลังอัปเดต</p>
              <p className="text-amber-600 text-sm">แอดมินกำลังแก้ไขข้อมูลบัญชี กรุณารอแอดมินยืนยันก่อนโอน แล้วเปิดลิงก์นี้อีกครั้ง</p>
            </div>
          ) : (
            <>
              {/* Transfer destination: bank info (copiable) */}
              {info.bankName && (
                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-500">โอนเข้าบัญชี</div>
                  <div className="px-4 py-3 space-y-2">
                    <Row label="ธนาคาร" value={info.bankName} />
                    {info.accountName && <Row label="ชื่อบัญชี" value={info.accountName} />}
                    {info.accountNumber && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                        <p className="text-xs text-gray-500 mb-0.5">เลขบัญชี</p>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-blue-900 text-2xl font-bold tracking-wide break-all">{info.accountNumber}</p>
                          <button onClick={() => copy('acc', info.accountNumber)}
                            className="shrink-0 px-3 py-2 rounded-lg bg-blue-700 text-white text-xs font-semibold">
                            {copied === 'acc' ? '✓ คัดลอก' : 'คัดลอก'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Transfer destination: PromptPay QR (tap or button to download) */}
              {info.qrUrl && (
                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-500">QR พร้อมเพย์ (แตะเพื่อดาวน์โหลด)</div>
                  <div className="px-4 py-3 flex flex-col items-center gap-3">
                    <img src={info.qrUrl} alt="PromptPay QR" onClick={downloadQr}
                      className="w-56 h-56 object-contain border border-gray-200 rounded-xl cursor-pointer" />
                    <button onClick={downloadQr}
                      className="w-full py-2.5 rounded-xl bg-indigo-600 text-white font-semibold text-sm">⬇️ ดาวน์โหลด QR</button>
                  </div>
                </div>
              )}

              {/* Attach receipt */}
              <form onSubmit={handleUpload} className="space-y-3 pt-1">
                {showReplaceForm && <p className="text-amber-600 text-sm font-medium">⚠️ แนบสลิปใหม่จะแทนที่สลิปเดิม</p>}
                <p className="text-sm font-semibold text-gray-700">แนบสลิปหลังโอนเงินแล้ว</p>
                <input ref={fileRef} type="file" accept="image/*,.pdf"
                  className="block w-full text-sm text-gray-500 file:mr-3 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700" />
                {uploadError && <p className="text-red-600 text-sm">{uploadError}</p>}
                <button type="submit" disabled={uploading}
                  className="w-full py-3.5 rounded-xl bg-blue-700 text-white font-bold disabled:opacity-50">
                  {uploading ? 'กำลังอัปโหลด...' : '📎 ส่งสลิป'}
                </button>
                {showReplaceForm && (
                  <button type="button" onClick={() => setShowReplaceForm(false)}
                    className="w-full py-2 rounded-xl border border-gray-300 text-gray-500 text-sm">ยกเลิก</button>
                )}
              </form>

              {/* Withdrawal only: can't transfer → return the money to the customer */}
              {info.isWithdrawal && !showReplaceForm && (
                <div className="pt-3 mt-1 border-t border-gray-100">
                  <p className="text-xs text-gray-400 mb-2">โอนเงินให้ลูกค้าไม่ได้? (เช่น ข้อมูลบัญชีไม่ถูกต้อง)</p>
                  {returnError && <p className="text-red-600 text-sm mb-2">{returnError}</p>}
                  <button type="button" onClick={handleReturn} disabled={returning}
                    className="w-full py-2.5 rounded-xl border border-red-300 text-red-600 font-medium disabled:opacity-50">
                    {returning ? 'กำลังคืนเงิน...' : '↩️ โอนไม่ได้ / คืนเงินให้ลูกค้า'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm gap-2">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="text-right text-gray-800 font-medium">{value}</span>
    </div>
  );
}
